import { useEffect, useId, useState } from 'react';
import styled, { keyframes } from 'styled-components';

const RING_RADIUS = 30;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const secondsUntil = (endsAt: number) => Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));

// The live API returns Korea time without an offset. Accept explicit ISO offsets too.
export function parseLiveClosingTime(value?: string): number | null {
  if (!value?.trim()) return null;
  const iso = value.trim().replace(' ', 'T');
  const timestamp = Date.parse(/(?:Z|[+-]\d{2}:?\d{2})$/i.test(iso) ? iso : `${iso}+09:00`);
  return Number.isFinite(timestamp) ? timestamp : null;
}

/** Displays the broadcast's scheduled deadline; the server owns automatic shutdown. */
export default function LiveClosingCountdown({ endsAt }: { endsAt: number }) {
  const [seconds, setSeconds] = useState(() => secondsUntil(endsAt));
  const gradientId = useId();

  useEffect(() => {
    setSeconds(secondsUntil(endsAt));
    if (endsAt <= Date.now()) return;

    const update = () => {
      const remaining = secondsUntil(endsAt);
      setSeconds(remaining);
      if (remaining === 0) {
        window.clearInterval(timer);
        document.removeEventListener('visibilitychange', update);
        window.removeEventListener('focus', update);
      }
    };
    const timer = window.setInterval(update, 1000);
    document.addEventListener('visibilitychange', update);
    window.addEventListener('focus', update);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', update);
      window.removeEventListener('focus', update);
    };
  }, [endsAt]);

  if (seconds > 600) return null;

  const phase = seconds === 0 ? 'ended' : seconds <= 10 ? 'urgent' : seconds <= 60 ? 'imminent' : 'calm';
  const progress = Math.min(seconds / 60, 1);
  const label = seconds === 0 ? '라이브 종료'
    : `마감 ${seconds > 60 ? `${Math.ceil(seconds / 60)}분` : `${seconds}초`} 전`;

  // Only the phase changes classes; per-second SVG values stay in inline styles.
  // This is an indicator, with no click action, so retain its existing timer role.
  return <Timer role="timer" aria-live="off" aria-label={seconds === 0 ? '라이브 종료' : `마감까지 ${seconds}초 남음`}
    className={`closing-timer--${phase}`}>
    <div className="closing-timer__face">
      <svg className="closing-timer__ring" viewBox="0 0 64 64" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" className="closing-timer__gradient-start" />
            <stop offset="1" className="closing-timer__gradient-end" />
          </linearGradient>
        </defs>
        <circle className="closing-timer__track" cx="32" cy="32" r={RING_RADIUS} />
        <circle className="closing-timer__progress" cx="32" cy="32" r={RING_RADIUS}
          stroke={`url(#${gradientId})`} strokeDasharray={RING_CIRCUMFERENCE}
          style={{ strokeDashoffset: RING_CIRCUMFERENCE * (1 - progress) }} />
      </svg>
      <svg className="closing-timer__icon" viewBox="0 0 96 96" aria-hidden="true" focusable="false">
        <g fill="currentColor">
          <circle cx="48" cy="53" r="25" />
          <rect x="40" y="16" width="16" height="7" rx="3.5" />
          <rect x="44.5" y="22" width="7" height="8" rx="1.5" />
          <rect x="69" y="30" width="6" height="11" rx="3" transform="rotate(45 72 35)" />
        </g>
        <g className="closing-timer__hand" style={{ transform: `rotate(${(1 - progress) * 360}deg)` }}>
          <path d="M48 53V35" stroke="var(--closing-timer-surface)" strokeWidth="5" strokeLinecap="round" />
        </g>
        <circle cx="48" cy="53" r="3.4" fill="var(--closing-timer-surface)" />
      </svg>
    </div>
    <span className="closing-timer__label" aria-live="off">{label}</span>
    <span className="closing-timer__announcement" role="status" aria-live="polite" aria-atomic="true">
      {phase === 'urgent' ? '라이브 종료까지 10초 이내로 남았습니다.' : phase === 'ended' ? '라이브 종료' : ''}
    </span>
  </Timer>;
}

const spin = keyframes`to { transform: rotate(360deg); }`;
const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 var(--closing-timer-pulse); }
  50% { box-shadow: 0 0 0 var(--closing-timer-pulse-size) transparent; }
`;
const shake = keyframes`
  0%, 100% { transform: rotate(0); }
  25% { transform: rotate(-8deg); }
  75% { transform: rotate(8deg); }
`;

const Timer = styled.div`
  --closing-timer-size: 56px;
  --closing-timer-ring-size: 64px;
  --closing-timer-ring-width: 3px;
  --closing-timer-icon-size: 30px;
  --closing-timer-label-size: 10.5px;
  --closing-timer-label-gap: 6px;
  --closing-timer-surface: #1E1E2A;
  --closing-timer-border: rgba(255, 255, 255, .08);
  --closing-timer-icon: #fff;
  --closing-timer-label: rgba(255, 255, 255, .72);
  --closing-timer-track: rgba(255, 255, 255, .1);
  --closing-timer-violet: #7B5CF0;
  --closing-timer-pink: #F2689B;
  --closing-timer-alert: #FF3B5C;
  --closing-timer-pulse: rgba(255, 59, 92, .55);
  --closing-timer-pulse-size: 9px;

  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: var(--closing-timer-size);
  margin-top: 4px;
  font-family: var(--font-Pretendard-Medium);
  white-space: nowrap;

  .closing-timer__face {
    position: relative;
    display: grid;
    place-items: center;
    box-sizing: border-box;
    width: var(--closing-timer-size);
    height: var(--closing-timer-size);
    border-radius: 50%;
    border: 1px solid var(--closing-timer-border);
    background: var(--closing-timer-surface);
    color: var(--closing-timer-icon);
  }
  .closing-timer__ring {
    position: absolute;
    top: 50%;
    left: 50%;
    width: var(--closing-timer-ring-size);
    height: var(--closing-timer-ring-size);
    transform: translate(-50%, -50%) rotate(-90deg);
    pointer-events: none;
  }
  .closing-timer__ring circle {
    fill: none;
    stroke-width: var(--closing-timer-ring-width);
    stroke-linecap: round;
  }
  .closing-timer__track { stroke: var(--closing-timer-track); }
  .closing-timer__gradient-start { stop-color: var(--closing-timer-violet); }
  .closing-timer__gradient-end { stop-color: var(--closing-timer-pink); }
  .closing-timer__icon {
    width: var(--closing-timer-icon-size);
    height: var(--closing-timer-icon-size);
    transform-origin: center;
  }
  .closing-timer__hand {
    transform-box: view-box;
    transform-origin: 48px 53px;
  }
  .closing-timer__label {
    margin-top: var(--closing-timer-label-gap);
    font-size: var(--closing-timer-label-size);
    font-weight: 500;
    line-height: normal;
    font-variant-numeric: tabular-nums;
    color: var(--closing-timer-label);
    text-align: center;
    text-shadow: 1px 1px 3px rgba(0, 0, 0, .8);
  }
  &.closing-timer--calm .closing-timer__hand { animation: ${spin} 60s linear infinite; }
  &.closing-timer--imminent, &.closing-timer--urgent {
    .closing-timer__progress { transition: stroke-dashoffset 1s linear; }
    .closing-timer__hand { transition: transform 1s linear; }
  }
  &.closing-timer--urgent {
    .closing-timer__face {
      border-color: var(--closing-timer-pulse);
      animation: ${pulse} 1s ease-in-out infinite;
    }
    .closing-timer__icon { animation: ${shake} .5s ease-in-out infinite; }
    .closing-timer__label {
      color: var(--closing-timer-alert);
      font-family: var(--font-Pretendard-Bold);
      font-weight: 700;
    }
    .closing-timer__progress { stroke: var(--closing-timer-alert); }
  }
  &.closing-timer--ended .closing-timer__progress { opacity: 0; }
  .closing-timer__announcement {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
  @media (prefers-reduced-motion: reduce) {
    .closing-timer__face, .closing-timer__icon, .closing-timer__hand {
      animation: none !important;
      transform: none !important;
      transition: none !important;
    }
    .closing-timer__progress { transition: none !important; }
  }
`;
